<template>
  <div class="scrollable-tabs-wrapper">
    <!-- Tab Headers -->
    <div
      ref="tabHeader"
      class="scrollable-tabs__header">
      <div
        v-for="(tab, index) in tabs"
        :key="index"
        :ref="el => { if (el) tabRefs[index] = el }"
        :class="['scrollable-tabs__tab', activeTabIndex === index ? 'scrollable-tabs__tab--active' : '']"
        @click.prevent.stop="scrollToTab(index)">
        <slot
          name="tab"
          :tab="tab"
          :index="index">
          {{ tab.name }}
        </slot>
      </div>
      <!-- Header spacer for proper scrolling -->
      <div class="scrollable-tabs__header-spacer" />
    </div>

    <!-- Scrollable Content -->
    <div
      ref="contentScroll"
      class="scrollable-tabs__content-scroll"
      @scroll="handleContentScroll">
      <div class="scrollable-tabs__content-container">
        <div
          v-for="(tab, tabIndex) in tabs"
          :id="'content-' + tabIndex"
          :key="tabIndex"
          :ref="el => { if (el) contentRefs[tabIndex] = el }"
          class="scrollable-tabs__content-section">
          <slot
            name="content"
            :tab="tab"
            :index="tabIndex" />

          <!-- Add extra padding to the last section -->
          <div
            v-if="tabIndex === tabs.length - 1"
            class="scrollable-tabs__extra-padding" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref, onMounted, watch, nextTick, onUnmounted,
} from 'vue'

defineOptions({
  name: 'ScrollableTabs',
})

const props = defineProps({
  tabs: {
    type: Array,
    required: true,
  },
  modelValue: {
    type: Number,
    default: 0,
  },
})

const emit = defineEmits(['update:modelValue'])

// Refs for DOM elements
const tabHeader = ref(null)
const contentScroll = ref(null)
const tabRefs = ref({})
const contentRefs = ref({})

// Store section positions for scroll spy
const sectionPositions = ref([])

// Active tab index
const activeTabIndex = ref(props.modelValue)

// calculateSectionPositions
// - 2025.07.25
// - Calculates and stores the positions of each tab section in the
//   scrollable content area. Determines the left position, width,
//   and right edge of each section for scroll spy functionality.
// - Clears previous positions and sorts sections by their left
//   position to ensure proper tab activation during scrolling.
const calculateSectionPositions = () => {
  sectionPositions.value = []

  if (!props.tabs || !props.tabs.length) return

  // Calculate the position of each section
  for (let i = 0; i < props.tabs.length; i++) {
    const element = contentRefs.value[i]

    if (!element) continue

    const position = {
      index: i,
      left: element.offsetLeft,
      width: element.offsetWidth,
      right: element.offsetLeft + element.offsetWidth,
    }

    sectionPositions.value.push(position)
  }

  // Sort sections by their left position (should already be in order, but just to be safe)
  sectionPositions.value.sort((a, b) => a.left - b.left)
}

// scrollToTab
// - 2025.07.25
// - Scrolls the content area to the tab section at the specified
//   index. Updates the active tab index if emitEvent is true.
// - Calculates the scroll position based on the content element's
//   offsetLeft and sets the scroll behavior to auto for immediate
//   scrolling. After a delay, recalculates section positions to
//   ensure scroll spy works correctly.
// - Parameters:
//   - index: The index of the tab to scroll to
//   - emitEvent: Whether to update the active tab index
const scrollToTab = (index, emitEvent = true) => {
  if (emitEvent) {
    activeTabIndex.value = index
  }

  // Find the content element at this index
  const contentElement = contentRefs.value[index]

  if (contentElement && contentScroll.value) {
    // Calculate the scroll position
    const scrollLeft = contentElement.offsetLeft

    // Use smooth scrolling behavior
    contentScroll.value.style.scrollBehavior = 'auto'

    // Set the scroll position
    contentScroll.value.scrollLeft = scrollLeft

    // Reset scroll behavior after a short delay
    setTimeout(() => {
      if (contentScroll.value) {
        contentScroll.value.style.scrollBehavior = 'auto'

        // Recalculate section positions after scrolling
        calculateSectionPositions()
      }
    }, 500)
  }
}

// handleContentScroll
// - 2025.07.25
// - Updates the active tab based on the current scroll position of
//   the content area. Determines which section is currently most
//   visible in the viewport and activates the corresponding tab.
// - Special case: When at the beginning of the content (scrollLeft
//   <= 5), always selects the first tab.
// - Parameters:
//   - event: The scroll event object containing scrollLeft
const handleContentScroll = (event) => {
  if (!props.tabs || !props.tabs.length || sectionPositions.value.length === 0) return

  // Get the current scroll position
  const scrollLeft = event.target.scrollLeft

  // Special case: if we're at the very beginning, always select the first tab
  if (scrollLeft <= 5) {
    if (activeTabIndex.value !== 0) {
      activeTabIndex.value = 0
    }

    return
  }

  // Find the appropriate section based on scroll position
  let newActiveIndex = 0

  // Loop through each section to find which one should be active
  for (let i = 0; i < sectionPositions.value.length; i++) {
    const section = sectionPositions.value[i]

    // If this is the last section
    if (i === sectionPositions.value.length - 1) {
      newActiveIndex = section.index
      break
    }

    // Get the next section
    const nextSection = sectionPositions.value[i + 1]

    // If the scroll position is between this section's left edge and the next section's left edge
    // then this section should be active
    if (scrollLeft >= section.left && scrollLeft < nextSection.left) {
      newActiveIndex = section.index
      break
    }
  }

  // Only update if the active tab would change
  if (activeTabIndex.value !== newActiveIndex) {
    activeTabIndex.value = newActiveIndex
  }
}

// resetRefs
// - 2025.07.25
// - Resets all reference objects used for tracking tabs and content
//   sections. Clears tabRefs, contentRefs, and sectionPositions to
//   prepare for recalculation or component updates.
const resetRefs = () => {
  tabRefs.value = {}
  contentRefs.value = {}
  sectionPositions.value = []
}

// scrollTabHeaderToActiveTab
// - 2025.07.25
// - Ensures the active tab is visible in the tab header by scrolling
//   the header if necessary. Calculates the position of the active
//   tab relative to the header's viewport and adjusts the scroll
//   position to make the tab fully visible.
// - Adds padding (20px) to provide better visual context around the
//   active tab when scrolling the header.
const scrollTabHeaderToActiveTab = () => {
  const activeTabElement = tabRefs.value[activeTabIndex.value]
  const headerElement = tabHeader.value

  if (activeTabElement && headerElement) {
    // Calculate the position of the active tab relative to the header
    const tabLeft = activeTabElement.offsetLeft
    const tabWidth = activeTabElement.offsetWidth
    const tabRight = tabLeft + tabWidth

    const headerScrollLeft = headerElement.scrollLeft
    const headerWidth = headerElement.clientWidth
    const headerScrollRight = headerScrollLeft + headerWidth

    // If the active tab is not fully visible, scroll the header
    if (tabLeft < headerScrollLeft) {
      // Tab is to the left of the visible area
      headerElement.scrollLeft = tabLeft - 20 // Add some padding
    }
    else if (tabRight > headerScrollRight) {
      // Tab is to the right of the visible area
      headerElement.scrollLeft = tabRight - headerWidth + 20 // Add some padding
    }
  }
}

// recalculatePositions
// - 2025.07.25
// - Triggers recalculation of section positions on the next DOM
//   update cycle. Used as a public method to allow parent components
//   to request position recalculation when content changes.
const recalculatePositions = () => {
  nextTick(() => {
    calculateSectionPositions()
  })
}

// forceScrollToActiveTab
// - 2025.07.25
// - Forces scrolling to the currently active tab and recalculates
//   all positions. Used when the component needs to ensure the
//   active tab is visible, such as after mounting or tab changes.
// - Checks if the active tab index is valid before attempting to
//   scroll, then calls multiple helper functions to ensure proper
//   positioning and visibility.
const forceScrollToActiveTab = () => {
  if (activeTabIndex.value >= 0 && props.tabs && props.tabs.length > activeTabIndex.value) {
    nextTick(() => {
      calculateSectionPositions()
      scrollToTab(activeTabIndex.value, false)
      scrollTabHeaderToActiveTab()
    })
  }
}

// Expose methods to parent component
defineExpose({
  forceScrollToActiveTab,
  scrollToTab,
  scrollTabHeaderToActiveTab,
  recalculatePositions,
})

// Resize timeout for debouncing
let resizeTimeout = null

// handleResize
// - 2025.07.25
// - Handles window resize events by debouncing and recalculating
//   section positions. Prevents excessive calculations during rapid
//   resize events by using a timeout delay of 200ms.
// - Important for maintaining correct tab activation when the
//   component's dimensions change due to window resizing.
const handleResize = () => {
  // Debounce the resize event to avoid excessive calculations
  if (resizeTimeout) clearTimeout(resizeTimeout)
  resizeTimeout = setTimeout(() => {
    calculateSectionPositions()
  }, 200)
}

// Initialize on mount
onMounted(() => {
  resetRefs()
  // Use double nextTick to ensure DOM is fully updated before calculating positions and scrolling
  nextTick(() => {
    nextTick(() => {
      calculateSectionPositions()
      forceScrollToActiveTab()
    })
  })

  // Add resize event listener to recalculate section positions
  window.addEventListener('resize', handleResize)
})

// Clean up on unmount
onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  if (resizeTimeout) clearTimeout(resizeTimeout)
})

// Watch for changes in the active tab index from parent
watch(() => props.modelValue, (newValue) => {
  if (newValue !== activeTabIndex.value) {
    activeTabIndex.value = newValue
    // Use nextTick to ensure DOM is updated before scrolling
    nextTick(() => {
      scrollToTab(newValue, false)
    })
  }
}, {
  immediate: true,
})

// Watch for changes in the active tab index locally
watch(activeTabIndex, (newValue) => {
  emit('update:modelValue', newValue)

  // Ensure the active tab is visible in the tab header
  nextTick(() => {
    scrollTabHeaderToActiveTab()
  })
}, {
  immediate: true,
})

// Watch for changes in tabs to reset references and recalculate positions
watch(() => props.tabs, () => {
  nextTick(() => {
    resetRefs()
    calculateSectionPositions()
    // Ensure we scroll to the active tab after recalculating positions
    scrollToTab(activeTabIndex.value, false)
  })
}, {
  deep: true,
})

// Watch for changes in tab content that might affect section positions
watch(() => contentScroll.value?.scrollWidth, () => {
  nextTick(() => {
    calculateSectionPositions()
  })
})
</script>

<style lang="scss" scoped>
.scrollable-tabs-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.scrollable-tabs__header {
  display: flex;
  overflow-x: auto;
  white-space: nowrap;
  padding-bottom: 10px;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  // margin-bottom: 10px;
}

.scrollable-tabs__header::-webkit-scrollbar {
  display: none; /* Hide scrollbar for Chrome/Safari */
}

.scrollable-tabs__tab {
  font-size: 1rem;
  color: var(--black-secondary);
  padding: 6px 0;
  margin-right: 20px;
  position: relative;
  cursor: pointer;
}

.scrollable-tabs__tab--active {
  font-size: 1.025rem;
  font-weight: bold;
  color: var(--black-primary);
}

.scrollable-tabs__tab--active::after {
  content: '';
  display: block;
  width: 50%;
  height: 3px;
  border-radius: 2px;
  background-color: #F24848;
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
}

.scrollable-tabs__content-scroll {
  flex: 1;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  scroll-behavior: auto; /* Will be set to smooth when needed via JS */
  padding: 0;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: none;
}

.scrollable-tabs__content-scroll::-webkit-scrollbar {
  display: none;
}

.scrollable-tabs__content-container {
  display: inline-flex;
  min-width: 100%;
}

.scrollable-tabs__content-section {
  white-space: normal;
  flex-shrink: 1;
  padding-bottom: env(safe-area-inset-bottom);
  display: flex;
  flex-flow: row nowrap;
}

.scrollable-tabs__extra-padding {
  width: 250px;
  height: 1px;
  display: inline-block;
}

.scrollable-tabs__header-spacer {
  min-width: 80px;
  height: 1px;
  flex-shrink: 0;
}

@media (max-width: 640px) {
  .scrollable-tabs__tab {
    font-size: .9rem;
  }

  .scrollable-tabs__tab--active {
    font-size: 1rem;
  }
}
</style>
